import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useStudentAuth } from "../StudentAuthContext";

export default function StudentLogin() {
  const { setSession } = useStudentAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = location.state?.next || "/book";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await api.studentLogin(email, password);
      setSession(data.token, data.student);
      navigate(next, { replace: true });
    } catch (err) {
      setError(err.message || "Could not sign in");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-[100dvh] bg-[#FAF9F6] pt-24 md:pt-32 pb-12 px-6" data-testid="student-login-page">
      <div className="max-w-md mx-auto">
        <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
          Sign in
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
          Welcome back.
        </h1>

        <form onSubmit={onSubmit} className="mt-10 space-y-4">
          <Field label="Email">
            <input
              data-testid="student-login-email"
              type="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Password">
            <input
              data-testid="student-login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
            />
          </Field>
          <div className="text-right -mt-1">
            <Link
              to="/forgot-password"
              className="text-[11px] tracking-[0.22em] uppercase text-[#1C1C1C]/55 hover:text-[#1C1C1C]"
              data-testid="student-login-forgot-link"
            >
              Forgot password?
            </Link>
          </div>
          {error && <div className="text-sm text-[#B83A3A]" data-testid="student-login-error">{error}</div>}
          <button
            data-testid="student-login-submit"
            type="submit"
            disabled={loading}
            className="w-full px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-8 text-sm text-[#1C1C1C]/65">
          New here?{" "}
          <Link
            to="/register"
            state={{ next }}
            className="underline underline-offset-4 text-[#1C1C1C]"
            data-testid="student-login-register-link"
          >
            Create an account
          </Link>
        </p>
        <style>{`.input{width:100%;padding:.75rem 1rem;background:white;border:1px solid rgba(28,28,28,0.1);border-radius:.75rem;font-size:1rem;outline:none}.input:focus{border-color:rgba(28,28,28,0.4)}`}</style>
      </div>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-2">{label}</div>
      {children}
    </label>
  );
}
